import * as React from 'react';
import { observer } from 'mobx-react';
import { UserOutlined } from '@ant-design/icons';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Input, Alert } from 'antd';
import { BetterFormComponent } from '../../../components/better-component/better-component';
import { Well } from '../../../components/well/well';
import { Translate } from '../../../components/translate/translate';
import { Container } from 'typescript-ioc/es5';
import { UserService, UserTokenType } from '../../../services/user';
import autobind from 'autobind-decorator';
import { UserDataForm, UserDataFormStyle } from '../../users';
import { AppService } from '../../../services';
import './forgot-password.scss';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import { simpleFormErrorNotification } from '_utils/utils';

export interface IForgotResetFormOptions {
    password: string;
    passwordVerify: string;
}

interface IProps extends FormComponentProps {}

interface IState {
    loading: boolean;
    hasError: boolean;
    errorMessage: string | React.ReactNode;
    user?: any;
    requestCompleted?: boolean;
}

@observer
export class ForgotPasswordForm extends BetterFormComponent<IProps, IState> {
    public state: IState = {
        loading: true,
        hasError: false,
        errorMessage: '',
        requestCompleted: false,
    };

    private appService: AppService;
    private userService: UserService;

    public constructor(props: IProps) {
        super(props);

        this.appService = Container.get(AppService);
        this.userService = Container.get(UserService);
    }

    public async componentDidMount() {
        if (this.queryString.token) {
            try {
                const user = await this.userService.confirmUserToken(
                    this.queryString.token as any,
                    UserTokenType.FORGOT_PASSWORD
                );

                this.setState({ user });
            } catch (error) {
                this.setState({
                    hasError: true,
                    errorMessage: error.message,
                });
            }
        }

        this.setState({ loading: false });
    }

    public render() {
        return this.queryString.token
            ? this.renderCompleteRequestForm()
            : this.renderInitiateRequestForm();
    }


    public renderInitiateRequestForm() {
        const wellProps: any = {};
        const wellClassnames: string[] = ['init-well'];

        if (this.state.requestCompleted) {
            wellClassnames.push('completed');
            wellProps.submitText = 'Return to Login';
            wellProps.showCancel = false;
        }

        return (
            <div className="forgot-password-page">
                <div className="forgot-password-page-content">
                    <div className="content-header">
                        <h1>Forgot Your Password?</h1>

                        <Well
                            className={wellClassnames.join(' ')}
                            ghost={true}
                            showHeader={false}
                            onSubmit={this.handleOnInitiateSubmit}
                            onCancel={this.handleCancel}
                            { ...wellProps }
                        >
                            {this.state.requestCompleted ? (
                                <div>
                                    A password reset email was sent successfully.
                                </div>
                            ) : (
                                <Form className="login-form hide-inline-errors">
                                    <div>
                                        Please enter your email address and we will send
                                        you a link to reset your password via email.
                                    </div>
                                    <br />

                                    <Form.Item>
                                        {this.getFieldDecorator('email', {
                                            rules: [
                                                {
                                                    type: 'email',
                                                    message: 'Email is not a valid email',
                                                },
                                            ],
                                        })(
                                            <Input
                                                size="large"
                                                prefix={<UserOutlined />}
                                                placeholder="Email"
                                            />
                                        )}
                                    </Form.Item>

                                    {this.state.hasError && (
                                        <Alert
                                            type="error"
                                            message={this.state.errorMessage}
                                        />
                                    )}
                                </Form>
                            )}
                        </Well>
                    </div>
                </div>
            </div>
        );
    }

    public renderCompleteRequestForm() {
        return this.state.loading ? '' : (
            <div className="forgot-password-page">
                <div className="forgot-password-page-content">
                    <div className="content-header">
                        <h1>
                            <Translate name="resetPassword.formHeader" />
                        </h1>
                    </div>

                    <UserDataForm
                        className="forgot-password-form"
                        style={UserDataFormStyle.PASSWORD_RESET}
                        hideWell={true}
                        onSubmit={this.handleOnCompleteSubmit}
                        submitText='Submit'
                        user={this.state.user}
                    />

                    {this.state.hasError && (
                        <Alert
                            className="forgot-password-alert"
                            type="error"
                            message={this.state.errorMessage}
                        />
                    )}
                </div>
            </div>
        );
    }

    @autobind
    private handleCancel(): void {
        this.appService.routeBack();
    }

    @autobind
    private async handleOnInitiateSubmit(): Promise<any> {
        if (this.state.requestCompleted) {
            return this.handleCancel();
        }

        this.setState({
            hasError: false,
            errorMessage: '',
        });

        let values: any;
        const preflightErrors: string[] = [];

        try {
            values = await this.validateFields();

            if (!values.email || values.email === '') {
                preflightErrors.push('Email is required');
            }
        } catch (error) {
            if (error.email) {
                preflightErrors.push(error.email.errors.map((err: any) => err.message));
            }
        }

        if (preflightErrors.length > 0) {
            this.setState({
                hasError: true,
                errorMessage: preflightErrors.join(', '),
            });

            return;
        }

        try {
            await this.userService.processUserForgotPassword(values.email);
            this.setState({ requestCompleted: true });
        } catch (error) {
            const errStatus = error.response.status;
            const errMessage = error.response.data?.message ?? 'An error has occurred.';

            this.setState({
                hasError: true,
                errorMessage: errStatus === 429 ? (
                    <div>
                        <div>Pushly was unable to complete your request.</div>
                        <div>{errMessage.replace(/\.$/, '')}.</div>
                    </div>
                ) : errMessage,
            });
        }
    }

    @autobind
    private async handleOnCompleteSubmit(error: any, values: IForgotResetFormOptions): Promise<any> {
        if (!error) {
            try {
                await this.userService.processUserPasswordReset(
                    this.queryString.token as any,
                    this.state.user.id,
                    values,
                );
            } catch (err) {
                const errStatus = err.response.status;
                let errMessage = err.response.data?.message ?? 'An error has occurred.';

                if (err.response.data?.details) {
                    const details = err.response.data?.details;
                    errMessage = Array.isArray(details) ? details.join(' ') : details;
                }

                this.setState({
                    hasError: true,
                    errorMessage: errStatus === 429 ? (
                        <div>
                            <div>Pushly was unable to complete your request.</div>
                            <div>{errMessage.replace(/\.$/, '')}.</div>
                        </div>
                    ) : errMessage,
                });
            }
        }
        else {
            simpleFormErrorNotification(error);
        }
    }
}

export const ForgotPassword = Form.create<IProps>()(ForgotPasswordForm);
